import { Component, forceUpdate, h, State, Prop, Watch } from '@stencil/core';
import { getAuthClient, AuthClientInstance } from '@trumpf/ux.utils';
import { i18nProvider } from '@trumpf/ux.i18n';
import loadTranslations, { TranslationKey, TranslationOptions } from '../../utils/loadTranslations';

@Component({
  tag: 'my-component',
  styleUrl: 'my-component.css',
  shadow: true,
})
export class MyComponent {
  private authClient: AuthClientInstance;
  private i18nSubscriberId: string;

  @Prop({ reflect: true }) locale: string = 'en-US';

  @State() token: string | null = null;
  @State() headline: string;
  @State() buttonLabel: string;
  @State() checkboxLabel: string;

  constructor() {
    this.authClient = getAuthClient(); // Initialize the auth client
  }

  componentDidLoad() {
    this.retrieveToken(); // Call to dynamically retrieve the token after the component loads
  }

  connectedCallback() {
    this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
  }

  disconnectedCallback() {
    i18nProvider.unsubscribe(this.i18nSubscriberId);
  }

  componentWillLoad() {
    this.loadTranslations();
  }

  @Watch('locale')
  onLocaleChange() {
    if (this.locale !== i18nProvider.language) {
      this.loadTranslations();
    }
  }

  private async retrieveToken() {
    try {
      let token = this.authClient.getAccessToken();

      if (!token) {
        console.warn('Token is not available yet. Waiting for initialization...');
        token = await this.waitForToken();
      }

      if (token) {
        console.log('Access Token:', token);
        this.token = token;
        sessionStorage.setItem('auth_token', token);
      } else {
        console.warn('Token is still unavailable after waiting.');
      }
    } catch (error) {
      console.error('Error retrieving access token:', error);
    }
  }

  private async waitForToken(maxRetries = 10, delayMs = 200): Promise<string | null> {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const token = this.authClient.getAccessToken();
      if (token) {
        return token;
      }
      await new Promise(resolve => setTimeout(resolve, delayMs)); // Delay before next attempt
    }
    return null;
  }

  private loadTranslations() {
    loadTranslations(this.locale).then(locale => {
      i18nProvider.changeLanguage(locale);
      this.locale = locale;
    });
  }

  render() {
    const t = (key: TranslationKey, options: TranslationOptions = { returnObjects: true }) => i18nProvider.t(`ai-in-service:${String(key)}`, options);
    let contentArray: string[] = [];
    try {
      const rawContent = t('legalDisclaimer.content');
      contentArray = Array.isArray(rawContent) ? rawContent : JSON.parse(rawContent);
    } catch (error) {
      console.error('Failed to parse content array:', error);
    }

    return (
      <div>
        <ux-u-silent-auth client-id="1kffflasH4444COTiFmP8DDO9ywa"></ux-u-silent-auth>
        {this.token && (
          <legal-disclaimer headline={t('legalDisclaimer.headline')} button-label={t('legalDisclaimer.buttonLabel')} checkbox-label={t('legalDisclaimer.checkboxLabel')}>
            {contentArray.map((content, index) => (
              <ux-copy variant="default" key={index}>
                {index === 1 ? <strong>{content}</strong> : content}
              </ux-copy>
            ))}
          </legal-disclaimer>
        )}
      </div>
    );
  }
}
